import React, { useState } from 'react';
import { NodeProperty } from '@shared/types/node-properties';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { EnhancedPropertyEditor } from '../EnhancedPropertyEditor';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';

interface CollectionPropertyRendererProps {
  property: NodeProperty;
  value: Record<string, any>[];
  onChange: (value: Record<string, any>[]) => void;
  disabled?: boolean;
  error?: string;
}

export const CollectionPropertyRenderer: React.FC<CollectionPropertyRendererProps> = ({
  property,
  value = [],
  onChange,
  disabled = false,
  error,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set([0]));

  const addItem = () => {
    const newItem: Record<string, any> = {};
    
    // Initialize with default values
    const fields = property.values || property.options || [];
    if (Array.isArray(fields)) {
      fields.forEach((prop: any) => {
        newItem[prop.key] = prop.default ?? '';
      });
    }

    const newValue = [...value, newItem];
    onChange(newValue);
    setExpandedItems(new Set([...expandedItems, newValue.length - 1]));
  };

  const removeItem = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
    
    // Update expanded items
    const newExpanded = new Set<number>();
    expandedItems.forEach((i) => {
      if (i < index) newExpanded.add(i);
      else if (i > index) newExpanded.add(i - 1);
    });
    setExpandedItems(newExpanded);
  };

  const updateItem = (index: number, key: string, itemValue: any) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [key]: itemValue };
    onChange(newValue);
  };

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const getItemLabel = (item: Record<string, any>, index: number): string => {
    // Try to find a meaningful label from the item
    const fields = property.values || property.options || [];
    if (Array.isArray(fields)) {
      const labelKey = fields.find((v: any) => v.type === 'string')?.key;
      if (labelKey && item[labelKey]) {
        return String(item[labelKey]);
      }
    }
    return `Item ${index + 1}`;
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Label>
            {property.label}
            {property.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          {property.description && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">{property.description}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          disabled={disabled}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Item
        </Button>
      </div>

      <div className="space-y-2">
        {value.map((item, index) => (
          <Card key={index} className="p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
                  <button
                    type="button"
                    onClick={() => toggleExpanded(index)}
                    className="text-sm font-medium hover:underline"
                  >
                    {getItemLabel(item, index)}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(index)}
                  disabled={disabled}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>

              {expandedItems.has(index) && (
                <div className="space-y-3 pl-6 border-l-2 border-muted">
                  {(() => {
                    const fields = property.values || property.options;
                    if (!Array.isArray(fields)) return null;
                    return fields.map((valueProp: any) => (
                      <EnhancedPropertyEditor
                        key={valueProp.key}
                        property={valueProp as any}
                        value={item[valueProp.key]}
                        onChange={(newValue) => updateItem(index, valueProp.key, newValue)}
                        disabled={disabled}
                      />
                    ));
                  })()}
                </div>
              )}
            </div>
          </Card>
        ))}

        {value.length === 0 && (
          <div className="text-center py-8 text-muted-foreground text-sm border-2 border-dashed rounded-md">
            No items added yet. Click "Add Item" to get started.
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
};
